import { FC, useCallback, useMemo, useRef, useState } from "react";
import Input from "../../../../components/Form/El/Input";
import { formatFileSize } from "helpers/utils";
import { setNotification } from "helpers/cache";
import {
  IScanResult,
  SCAN_MAX_FILES,
  UploadEntry,
  scanDataTransfer,
  scanFileList,
} from "./upload/scanUpload";

// Rows drawn for a large selection; the summary line above them carries the totals
const MAX_ROWS = 50;

/**
 * File picker shared by the ArtWork form and the two file cards on the Misc tab.
 *
 * Two contracts live here. The original one, `filesList` + `onChange(File[])`, is what ArtWork
 * uses and is untouched. The cards pass `entries` + `onEntriesChange` instead: each File comes
 * with the folder it was picked from, kept next to the File rather than on it (nothing upstream
 * guarantees File identity through maps and clones), and the folders are reported on their own
 * so an empty one from a drop still gets created.
 */
const EditQuoteInputFiles: FC<{
  onChange?: (files: File[]) => void;
  fileComment: {
    value: string;
    onChange: (newVal: string) => void;
  };
  filesList?: File[];
  filesVisForSuppliers?: {
    value: number[],
    onChange: (value: number[]) => void;
  }
  inputId?: string;
  // Entries mode: when both are given, filesList/onChange are not used
  entries?: UploadEntry[];
  // Folders the upload will create, including empty ones a drop found
  directories?: string[];
  onEntriesChange?: (entries: UploadEntry[], directories: string[]) => void;
  // Accept dropped folders and offer the folder picker (entries mode only)
  enableFolders?: boolean;
}> = ({
  onChange,
  fileComment,
  filesList = [],
  filesVisForSuppliers,
  inputId = "quote-file-input",
  entries,
  directories = [],
  onEntriesChange,
  enableFolders = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const isEntriesMode = !!entries && !!onEntriesChange;
  const canPickFolders = isEntriesMode && enableFolders;
  const folderInputRef = useRef<HTMLInputElement>(null);
  // A drop scan resolves asynchronously; merging into the latest props rather than the ones
  // captured at drop time keeps a second drop from discarding the first
  const latestRef = useRef({ entries, directories, onEntriesChange });
  latestRef.current = { entries, directories, onEntriesChange };

  const currentInputPlaceholder = useMemo(() => {
    if (isDragging) {
      return canPickFolders
        ? 'Drop files or folders to upload...'
        : 'Drop files to upload...'
    }

    return canPickFolders
      ? 'Drop files or folders, or click to upload...'
      : 'Drop files or click to upload...'
  }, [isDragging, canPickFolders]);

  const onUpdateFiles = useCallback((newFilesList: FileList|null) => {
    if (onChange && newFilesList && !!newFilesList.length) {
      onChange([...filesList, ...Array.from(newFilesList)]);
    }
  }, [filesList, onChange])

  const onAddScan = useCallback((result: IScanResult) => {
    const latest = latestRef.current;
    if (!latest.entries || !latest.onEntriesChange) return;
    if (!result.entries.length && !result.directories.length) return;

    // Folder names are unioned so two drops one after the other keep both trees
    const nextDirectories = latest.directories.slice();
    result.directories.forEach((path) => {
      if (nextDirectories.indexOf(path) === -1) nextDirectories.push(path);
    });
    latest.onEntriesChange(latest.entries.concat(result.entries), nextDirectories);

    if (result.truncated) {
      setNotification([
        {
          type: "ERROR",
          message: `Only the first ${SCAN_MAX_FILES} files were added. Upload the rest separately.`,
        },
      ]);
    }
  }, []);

  const summary = useMemo(() => {
    if (!entries) return "";
    let bytes = 0;
    entries.forEach((entry) => {
      bytes += entry.file.size;
    });
    const folders = directories.length;
    return (
      `${entries.length} file${entries.length === 1 ? "" : "s"}` +
      (folders ? `, ${folders} folder${folders === 1 ? "" : "s"}` : "") +
      `, ${formatFileSize(bytes)}`
    );
  }, [entries, directories]);

  return (
    <div
      onDragLeave={(event) => {
        event.preventDefault();
        setIsDragging(false);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDrop={(event) => {
        event.preventDefault();
        if (!isEntriesMode) {
          onUpdateFiles(event.dataTransfer.files);
          setIsDragging(false);
          return;
        }
        setIsDragging(false);
        if (canPickFolders) {
          // scanDataTransfer takes the item list before its first await; the list is empty
          // once this handler has returned
          scanDataTransfer(event.dataTransfer)
            .then(onAddScan)
            .catch((error: unknown) => {
              setNotification([
                {
                  type: "ERROR",
                  message: `Could not read the dropped items: ${
                    (error as Error)?.message || "unknown error"
                  }`,
                },
              ]);
            });
          return;
        }
        onAddScan(scanFileList(event.dataTransfer.files));
      }}
      className={`edit-quote__files-wrapper ${
          isDragging ? "edit-quote__files-wrapper_dragging" : ""
      }`}
    >
      <label
        htmlFor={inputId}
        className='edit-quote__file-input'
      >
        <span className="btn btn-small">
          <i className="icon-upload" />
        </span>
        <span>{currentInputPlaceholder}</span>
        <span className="btn btn-small btn-blue">Add File</span>
        {canPickFolders && (
          <button
            type="button"
            className="btn btn-small btn-blue"
            onClick={(event) => {
              // Inside the label: without this the click would open the file picker as well
              event.preventDefault();
              event.stopPropagation();
              folderInputRef.current?.click();
            }}
          >
            Add Folder
          </button>
        )}
        <input
          type="file"
          hidden
          id={inputId}
          multiple
          onChange={(value) => {
            const newFiles = value.target.files;

            if (isEntriesMode) {
              onAddScan(scanFileList(newFiles || []));
              // Let the same files be picked again after a row was removed
              value.target.value = "";
              return;
            }
            onUpdateFiles(newFiles);
          }}
        />
      </label>
      {canPickFolders && (
        <input
          type="file"
          hidden
          multiple
          ref={folderInputRef}
          id={`${inputId}-folder`}
          // Not in @types/react 18.0; the attribute is what turns the picker into a folder picker
          {...({ webkitdirectory: "", directory: "" } as any)}
          onChange={(event) => {
            onAddScan(scanFileList(event.target.files || []));
            event.target.value = "";
          }}
        />
      )}
      <Input
        value={fileComment.value}
        onChange={(value) => {
          fileComment.onChange(value.toString());
        }}
        name={`${inputId}-comment`}
        placeholder="Type comment here..."
        isTextarea={true}
        rowCount={2}
      />

      {!isEntriesMode && !!filesList.length && (
        <div className="edit-quote__file-list">
          <table className="table table_collapse">
            <thead className="table__thead_gray">
              <tr>
                <th>Title</th>
                <th>Comment</th>
                {!!filesVisForSuppliers && <th className="w-[100px]">for Suppliers</th>}
                <th className="w-[40px]"></th>
              </tr>
            </thead>

            <tbody>
              {filesList.map((file, index) => (
                <tr key={`edit-quote__file-list-item-${index}-${file.name}`}>
                  <td>{file.name}</td>
                  <td>{fileComment.value || "no comment"}</td>
                  {!!filesVisForSuppliers && (
                    <td>
                      <div className="flex justify-center">
                        <input
                          type="checkbox"
                          checked={filesVisForSuppliers.value.includes(index)}
                          onChange={() => {
                            const newValue = filesVisForSuppliers.value.includes(index)
                              ? filesVisForSuppliers.value.filter((indx) => indx != index)
                              : [ ...filesVisForSuppliers.value, index];
                            filesVisForSuppliers.onChange(newValue);
                          }}
                        />
                      </div>
                    </td>
                  )}
                  <td>
                    <div className="flex justify-center">
                      <button
                        className="btn btn-mini btn-warning"
                        onClick={() => {
                          if (onChange) onChange(filesList.filter((_, i) => i !== index));
                        }}
                      >
                        <i className="icon-trash-empty" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

        </div>
      )}

      {!!entries && !!onEntriesChange && (!!entries.length || !!directories.length) && (
        <div className="edit-quote__file-list">
          <div className="flex items-center justify-between gap-2 py-1 text-xs text-gray-500">
            <span>{summary}</span>
            <button
              type="button"
              className="btn btn-mini"
              onClick={() => onEntriesChange([], [])}
            >
              <i className="icon-cancel pr-1" />
              Clear
            </button>
          </div>
          {!!entries.length && (
            <table className="table table_collapse">
              <thead className="table__thead_gray">
                <tr>
                  <th>Title</th>
                  <th className="w-[90px]">Size</th>
                  <th className="w-[40px]"></th>
                </tr>
              </thead>

              <tbody>
                {entries.slice(0, MAX_ROWS).map((entry, index) => (
                  <tr key={`edit-quote__file-list-item-${index}-${entry.file.name}`}>
                    <td className="break-all">
                      {entry.path ? `${entry.path}/${entry.file.name}` : entry.file.name}
                    </td>
                    <td className="whitespace-nowrap">{formatFileSize(entry.file.size)}</td>
                    <td>
                      <div className="flex justify-center">
                        <button
                          className="btn btn-mini btn-warning"
                          onClick={() => {
                            onEntriesChange(entries.filter((_, i) => i !== index), directories);
                          }}
                        >
                          <i className="icon-trash-empty" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {entries.length > MAX_ROWS && (
            <div className="py-1 text-xs text-gray-500">+{entries.length - MAX_ROWS} more</div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditQuoteInputFiles;
